import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PropertyCard from '../components/PropertyCard';
import ProjectCard from '../components/ProjectCard';
import BuilderCard from '../components/BuilderCard';
import FeaturedPropertyCard from '../components/FeaturedPropertyCard';
import ShimmerCard from '../components/ShimmerCard';
import { FiMapPin, FiArrowRight, FiCheckCircle, FiSearch, FiBriefcase, FiUser, FiActivity, FiHeart, FiCamera } from 'react-icons/fi';
import axios from 'axios';
import { generateSlug } from '../utils/slugify';
import { getImageUrl, getDefaultPropertyImage } from '../utils/imageUtils';
import './Home.css';

let homeCache = null;

const DesktopHome = () => {
  const [properties, setProperties] = useState(homeCache ? homeCache.properties : []);
  const [featuredProperties, setFeaturedProperties] = useState(homeCache ? homeCache.featuredProperties : []);
  const [featuredProjects, setFeaturedProjects] = useState(homeCache ? homeCache.featuredProjects : []);
  const [projects, setProjects] = useState(homeCache ? homeCache.projects : []);
  const [builders, setBuilders] = useState(homeCache ? homeCache.builders : []);
  const [templates, setTemplates] = useState(homeCache ? homeCache.templates : []);
  const [banners, setBanners] = useState(homeCache ? homeCache.banners : []);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const [siteStats, setSiteStats] = useState(homeCache ? homeCache.siteStats : null);
  const [loading, setLoading] = useState(homeCache ? false : true);
  
  // Search States
  const [searchCountry, setSearchCountry] = useState('');
  const [searchState, setSearchState] = useState('');
  const [searchCity, setSearchCity] = useState('');
  const [searchType, setSearchType] = useState('');
  const [locationData, setLocationData] = useState(homeCache ? homeCache.locationData : {});
  const [isLocating, setIsLocating] = useState(false);
  
  const navigate = useNavigate();

  const countries = Object.keys(locationData).sort((a, b) => a.localeCompare(b));
  const states = searchCountry && locationData[searchCountry] ? Object.keys(locationData[searchCountry]).sort((a, b) => a.localeCompare(b)) : [];
  const cities = searchState && locationData[searchCountry] && locationData[searchCountry][searchState] ? [...locationData[searchCountry][searchState]].sort((a, b) => a.localeCompare(b)) : [];

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!homeCache) setLoading(true);
        const [propRes, projRes, builderRes, tempRes, bannerRes, locRes, statsRes, wealthCustomersRes, wealthAgentsRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/api/properties`),
          axios.get(`${process.env.REACT_APP_API_URL}/api/projects`),
          axios.get(`${process.env.REACT_APP_API_URL}/api/users/builders`),
          axios.get(`${process.env.REACT_APP_API_URL}/api/templates`),
          axios.get(`${process.env.REACT_APP_API_URL}/api/banners`),
          axios.get(`${process.env.REACT_APP_API_URL}/api/locations`),
          axios.get(`${process.env.REACT_APP_API_URL}/api/stats`).catch(() => ({ data: null })),
          axios.get('https://api.wealthassociate.in/customer/allcustomers').catch(() => ({ data: null })),
          axios.get('https://api.wealthassociate.in/agent/allagents').catch(() => ({ data: null }))
        ]);
        // Fetch featured properties directly from Wealth Associates API
        let fetchedFeaturedProps = [];
        try {
          const featuredRes = await axios.get('https://api.wealthassociate.in/properties/getApproveProperty');
          const data = Array.isArray(featuredRes.data) ? featuredRes.data : featuredRes.data.properties || [];
          fetchedFeaturedProps = data.map(p => ({ ...p, isWealthProperty: true }));
        } catch (err) {
          console.log("WealthBackend properties not accessible:", err.message);
        }

        // Fetch all types of projects from Wealth Associates API
        let fetchedFeaturedProjects = [];
        try {
          const endpoints = [
            'https://api.wealthassociate.in/coreproject/getallcoreprojects',
            'https://api.wealthassociate.in/coreproject/getallValueprojects',
            'https://api.wealthassociate.in/coreproject/getallWealthprojects',
            'https://api.wealthassociate.in/coreproject/getallPromotionalprojects'
          ];
          
          const responses = await Promise.allSettled(endpoints.map(url => axios.get(url)));
          
          responses.forEach(res => {
            if (res.status === 'fulfilled') {
              const data = Array.isArray(res.value.data) ? res.value.data : res.value.data.projects || [];
              fetchedFeaturedProjects = [...fetchedFeaturedProjects, ...data.map(p => ({ ...p, isWealthProject: true }))];
            }
          });
        } catch (err) {
          console.log("WealthBackend projects not accessible:", err.message);
        }

        const newCache = {
          properties: propRes.data,
          featuredProperties: fetchedFeaturedProps,
          projects: projRes.data,
          featuredProjects: fetchedFeaturedProjects,
          builders: builderRes.data,
          templates: tempRes.data,
          banners: bannerRes.data,
          locationData: locRes.data,
          siteStats: null
        };

        if (statsRes.data) {
          const stats = { ...statsRes.data };
          stats.properties = {};
          
          const localBuyers = stats.users?.buyer || 0;
          const wealthBuyers = wealthCustomersRes?.data?.count || 0;
          
          const localAgents = stats.users?.agent || 0;
          const wealthAgents = wealthAgentsRes?.data?.total || wealthAgentsRes?.data?.count || 0;
          
          const uniqueDevelopers = new Set();
          if (projRes.data && Array.isArray(projRes.data)) {
            projRes.data.forEach(p => { if (p.builderName) uniqueDevelopers.add(p.builderName); });
          }
          fetchedFeaturedProjects.forEach(p => {
            if (p.companyName) uniqueDevelopers.add(p.companyName);
          });
          const developersCount = uniqueDevelopers.size;

          stats.users = {
            ...stats.users,
            buyer: localBuyers + wealthBuyers,
            agent: localAgents + wealthAgents,
            builder: developersCount > 0 ? developersCount : (stats.users?.builder || 0)
          };

          const totalProperties = (propRes.data?.length || 0) + (fetchedFeaturedProps.length || 0);
          const totalProjects = (projRes.data?.length || 0) + (fetchedFeaturedProjects.length || 0);

          if (totalProperties > 0) {
            stats.properties['Properties'] = {
              uploaded: totalProperties + 86,
              available: totalProperties,
              sold: 86
            };
          }
          if (wealthCustomersRes.data?.total) {
            stats.properties.totalCustomers = wealthCustomersRes.data.total;
          }
          if (wealthAgentsRes.data?.total) {
            stats.properties.totalAgents = wealthAgentsRes.data.total;
          }
          newCache.siteStats = stats;
        }

        homeCache = newCache;

        setProperties(newCache.properties);
        setFeaturedProperties(newCache.featuredProperties);
        setProjects(newCache.projects);
        setFeaturedProjects(newCache.featuredProjects);
        setBuilders(newCache.builders);
        setTemplates(newCache.templates);
        setBanners(newCache.banners);
        setLocationData(newCache.locationData);
        if (newCache.siteStats) setSiteStats(newCache.siteStats);

      } catch (err) {
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    let interval;
    if (banners.length > 1) {
      interval = setInterval(() => {
        setCurrentBannerIndex(prev => (prev + 1) % banners.length);
      }, 5000);
    }
    return () => clearInterval(interval);
  }, [banners]);

  // Fetch banners specifically when searchCity changes
  useEffect(() => {
    const fetchLocationBanners = async () => {
      try {
        const url = searchCity 
          ? `${process.env.REACT_APP_API_URL}/api/banners?location=${encodeURIComponent(searchCity)}` 
          : `${process.env.REACT_APP_API_URL}/api/banners`;
        const res = await axios.get(url);
        setBanners(res.data);
        setCurrentBannerIndex(0); // Reset index
      } catch (error) {
        console.error('Error fetching location banners:', error);
      }
    };
    fetchLocationBanners();
  }, [searchCity]);

  const handleBannerClick = (url) => {
    if (!url) return;
    if (url.startsWith('http')) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      navigate(url);
    }
  };

  const handleSearch = () => {
    let queryParams = [];
    if (searchCountry) queryParams.push(`country=${encodeURIComponent(searchCountry)}`);
    if (searchState) queryParams.push(`state=${encodeURIComponent(searchState)}`);
    if (searchCity) queryParams.push(`city=${encodeURIComponent(searchCity)}`);
    
    if (searchType) {
      if (searchType.includes('|')) {
        const [pType, sType] = searchType.split('|');
        queryParams.push(`propertyType=${encodeURIComponent(pType)}`);
        queryParams.push(`subCategories=${encodeURIComponent(sType)}`);
      } else {
        queryParams.push(`propertyType=${encodeURIComponent(searchType)}`);
      }
    }
    navigate(`/properties?${queryParams.join('&')}`);
  };

  useEffect(() => {
    if (Object.keys(locationData).length > 0 && !searchCountry) {
      // Create a silent version of detect location that doesn't alert on error
      if (!navigator.geolocation) return;
      
      navigator.geolocation.getCurrentPosition(async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&email=admin@realproperties.com&accept-language=en`);
          const data = await res.json();
          
          if (data && data.address) {
            const fetchedCountry = data.address.country;
            const fetchedState = data.address.state;
            
            if (fetchedCountry) {
              const countryMatch = Object.keys(locationData).find(c => c.toLowerCase() === fetchedCountry.toLowerCase());
              if (countryMatch) {
                setSearchCountry(countryMatch);
                
                if (fetchedState) {
                  const stateMatch = Object.keys(locationData[countryMatch] || {}).find(s => s.toLowerCase() === fetchedState.toLowerCase());
                  if (stateMatch) {
                    setSearchState(stateMatch);
                    
                    // Robust City Matching: Check ONLY specific city-related geographic labels returned by Nominatim
                    const cityFields = ['city', 'town', 'village', 'municipality', 'county', 'suburb', 'neighbourhood', 'state_district', 'district'];
                    const addressValues = cityFields.map(field => data.address[field]).filter(val => typeof val === 'string');
                    const cityList = locationData[countryMatch][stateMatch] || [];
                    
                    let bestCityMatch = null;
                    
                    for (const val of addressValues) {
                      // Skip country and state to avoid false positives on city
                      if (val === fetchedCountry || val === fetchedState) continue;
                      
                      // 1. Try exact match
                      let match = cityList.find(c => c.toLowerCase() === val.toLowerCase());
                      
                      // 2. Try fuzzy match
                      if (!match) {
                        match = cityList.find(c => 
                          val.toLowerCase().includes(c.toLowerCase()) || 
                          c.toLowerCase().includes(val.toLowerCase())
                        );
                      }
                      
                      if (match) {
                        bestCityMatch = match;
                        break; // Stop at first valid match
                      }
                    }
                    
                    if (bestCityMatch) {
                      setSearchCity(bestCityMatch);
                    } else {
                      console.log("Could not match any of these to a city in DB:", addressValues);
                    }
                  }
                }
              }
            }
          }
        } catch (err) {
          // Silently handle API failures (like 429 Too Many Requests)
        }
      }, () => {
        // Silently fail if user denies permission on load
      });
    }
  }, [locationData]);

  const reversedFeatured = [...featuredProperties].reverse();
  const allPropertiesForSale = properties.filter(p => p.for !== 'Rent');
  const allWealthPropertiesForSale = reversedFeatured.filter(p => p.propertyType !== 'Rent');
  const totalSaleCount = allPropertiesForSale.length + allWealthPropertiesForSale.length;
  
  const allPropertiesForRent = properties.filter(p => p.for === 'Rent');
  const allWealthPropertiesForRent = reversedFeatured.filter(p => p.propertyType === 'Rent');
  const totalRentCount = allPropertiesForRent.length + allWealthPropertiesForRent.length;

  const reversedFeaturedProjects = [...featuredProjects].reverse();
  const totalProjectCount = projects.length + reversedFeaturedProjects.length;

  const totalBuilderCount = builders.length;

  const sortByImageCount = (a, b) => {
    const getImageCount = (p) => {
      let count = 0;
      if (p.images && p.images.length > 0) count += p.images.length;
      else if (p.coverImage || p.photo) count += 1;
      return count;
    };
    const countA = getImageCount(a);
    const countB = getImageCount(b);
    if (countA !== countB) return countB - countA;
    const dateA = new Date(a.createdAt || 0).getTime();
    const dateB = new Date(b.createdAt || 0).getTime();
    return dateB - dateA;
  };

  const newProperties = [...allPropertiesForSale, ...allWealthPropertiesForSale].sort(sortByImageCount).slice(0, 3);
  const rentalProperties = [...allPropertiesForRent, ...allWealthPropertiesForRent].sort(sortByImageCount).slice(0, 3);
  const combinedProjects = [...projects, ...reversedFeaturedProjects].slice(0, 3);

  const addedToday = [...properties.slice(3, 6), ...reversedFeatured.slice(3, 6)].sort(sortByImageCount).slice(0, 3);

  // Location-based filtering: only show if matching properties exist in the detected city
  const { nearbyProperties, locationLabel } = (() => {
    const city = searchCity?.toLowerCase();

    if (city) {
      // Local DB properties matching the city
      const localMatches = properties.filter(p =>
        p.address?.city?.toLowerCase().includes(city)
      );
      // Wealth Associates properties matching the city (stored as p.location string)
      const wealthMatches = featuredProperties.filter(p =>
        p.location?.toLowerCase().includes(city)
      ).map(p => ({ ...p, isWealthProperty: true }));

      const combined = [...localMatches, ...wealthMatches].sort(sortByImageCount).slice(0, 3);
      if (combined.length > 0) {
        return { nearbyProperties: combined, locationLabel: searchCity };
      }
    }

    return { nearbyProperties: [], locationLabel: '' };
  })();

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-full">
        {/* Full width banners */}
        <div className="banner-carousel-container">
          {banners.length > 0 ? (
            <>
              {banners.map((banner, index) => (
                <img
                  key={banner._id}
                  src={getImageUrl(banner.imageUrl)}
                  alt="Banner"
                  className={`banner-slide ${index === currentBannerIndex ? 'active' : ''}`}
                />
              ))}
              {banners.length > 1 && (
                <div className="banner-indicators">
                  {banners.map((_, i) => (
                    <button 
                      key={i} 
                      className={`banner-dot ${i === currentBannerIndex ? 'active' : ''}`}
                      onClick={() => setCurrentBannerIndex(i)}
                      aria-label={`Slide ${i + 1}`}
                    />
                  ))}
                </div>
              )}
            </>
          ) : (
            <img src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1600&q=80&fm=webp" fetchPriority="high" alt="Building" className="banner-slide active" />
          )}
          {/* Overlay to make text readable */}
          <div 
            className="banner-overlay" 
            onClick={() => banners.length > 0 && handleBannerClick(banners[currentBannerIndex]?.linkUrl)}
            style={{ cursor: banners.length > 0 && banners[currentBannerIndex]?.linkUrl ? 'pointer' : 'default' }}
          ></div>
        </div>

        <div className="hero-content" style={{ pointerEvents: 'none' }}>
          {banners.length > 0 ? (
            <>
              {banners[currentBannerIndex]?.title && (
                <h1>{banners[currentBannerIndex].title}</h1>
              )}
              {banners[currentBannerIndex]?.description && (
                <p>
                  {banners[currentBannerIndex].description.length > 150 
                    ? banners[currentBannerIndex].description.substring(0, 150) + '...' 
                    : banners[currentBannerIndex].description}
                </p>
              )}
            </>
          ) : (
            <>
              <h1>Easy way to find a perfect property</h1>
              <p>We provide a complete service for the sale, purchase or rental of real estate. We have been operating more than 10 years. Search millions of apartments and houses on Real Properties.</p>
            </>
          )}
        </div>
      </section>

      {/* Search Bar Section */}
      <section className="search-section-wrap">
        <div className="container relative">
          <div className="search-box-row">
            <select className="search-select" value={searchCountry} onChange={e => {
              setSearchCountry(e.target.value);
              setSearchState('');
              setSearchCity('');
            }}>
              <option value="">Country</option>
              {countries.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            
            <div className="search-divider"></div>
            
            <select className="search-select" value={searchState} onChange={e => {
              setSearchState(e.target.value);
              setSearchCity('');
            }} disabled={!searchCountry}>
              <option value="">State</option>
              {states.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            
            <div className="search-divider"></div>
            
            <select className="search-select" value={searchCity} onChange={e => setSearchCity(e.target.value)} disabled={!searchState || cities.length === 0}>
              <option value="">{searchState && cities.length === 0 ? "No cities available" : "City"}</option>
              {cities.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            
            <div className="search-divider"></div>
            
            <select 
              className="search-select"
              value={searchType}
              onChange={(e) => setSearchType(e.target.value)}
            >
              <option value="">Property Type</option>
              {[...templates].sort((a, b) => {
                if (a.name === 'Residential') return -1;
                if (b.name === 'Residential') return 1;
                return a.name.localeCompare(b.name);
              }).map(t => (
                <optgroup key={t.name} label={t.name}>
                  {t.subCategories && t.subCategories.map(sub => (
                    <option key={sub.name} value={`${t.name}|${sub.name}`}>{sub.name}</option>
                  ))}
                </optgroup>
              ))}
            </select>
            
            <div className="search-divider"></div>
            
            <button onClick={handleSearch} className="btn bg-primary text-white rounded-lg search-btn font-bold transition-transform hover:scale-105 shadow-lg flex-shrink-0">Search</button>
          </div>
        </div>
      </section>



      {/* Top Projects */}
      {(loading || combinedProjects.length > 0) && (
        <section className="top-offers py-3 md:py-5 bg-white">
          <div className="container">
            <div className="flex justify-between items-end mb-6">
              <div>
                <h2 className="text-3xl font-bold text-dark">Top Projects</h2>
              </div>
              <Link to="/projects" className="text-primary font-semibold flex items-center gap-1">View all ({totalProjectCount})</Link>
            </div>
            
            {loading ? (
              <div className="grid-3">
                {Array(3).fill().map((_, i) => <ShimmerCard key={i} />)}
              </div>
            ) : (
              <div className="grid-3">
                {combinedProjects.map((project, i) => (
                  <ProjectCard key={project._id || i} project={project} />
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Properties Near You */}
      {!loading && nearbyProperties.length > 0 && (
        <section className="top-offers py-3 md:py-5 bg-orange-50 border-t-2 border-orange-100">
          <div className="container">
            <div className="flex justify-between items-end mb-6">
              <div>
                <div className="text-xs font-bold text-primary uppercase tracking-wider mb-1 flex items-center gap-1"><FiMapPin size={12}/> Your Location</div>
                <h2 className="text-3xl font-bold text-dark">Properties Near You <span className="text-primary text-xl font-semibold">in {locationLabel}</span></h2>
              </div>
              <Link to={`/properties?city=${encodeURIComponent(searchCity || '')}&state=${encodeURIComponent(searchState || '')}`} className="text-primary font-semibold flex items-center gap-1">View all <FiArrowRight size={14}/></Link>
            </div>
            <div className="grid-3">
              {nearbyProperties.map(prop =>
                prop.isWealthProperty
                  ? <FeaturedPropertyCard key={`wealth-${prop._id}`} property={prop} />
                  : <PropertyCard key={prop._id} property={prop} />
              )}
            </div>
          </div>
        </section>
      )}

      {/* Top Offers */}
      {(loading || newProperties.length > 0) && (
        <section className="top-offers py-3 md:py-5 bg-white">
          <div className="container">
            <div className="flex justify-between items-end mb-6">
              <div>
                <h2 className="text-3xl font-bold text-dark">New Properties</h2>
              </div>
              <Link to="/properties" className="text-primary font-semibold flex items-center gap-1">View all ({totalSaleCount})</Link>
            </div>
            
            {loading ? (
              <div className="grid-3">
                {Array(3).fill().map((_, i) => <ShimmerCard key={i} />)}
              </div>
            ) : (
              <div className="grid-3">
                {newProperties.map(prop => (
                  prop.isWealthProperty ?
                    <FeaturedPropertyCard key={`wealth-${prop._id}`} property={prop} /> :
                    <PropertyCard key={prop._id} property={prop} />
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Rental Properties */}
      {(loading || rentalProperties.length > 0) && (
        <section className="top-offers py-3 md:py-5 bg-white">
          <div className="container">
            <div className="flex justify-between items-end mb-6">
              <div>
                <h2 className="text-3xl font-bold text-dark">Rental Properties</h2>
              </div>
              <Link to="/rent" className="text-primary font-semibold flex items-center gap-1">View all ({totalRentCount})</Link>
            </div>
            
            {loading ? (
              <div className="grid-3">
                {Array(3).fill().map((_, i) => <ShimmerCard key={i} />)}
              </div>
            ) : (
              <div className="grid-3">
                {rentalProperties.map(prop => (
                  prop.isWealthProperty ?
                    <FeaturedPropertyCard key={`wealth-${prop._id}`} property={prop} /> :
                    <PropertyCard key={prop._id} property={prop} />
                ))}
              </div>
            )}
          </div>
        </section>
      )}



      {/* Top Builders */}
      {(loading || builders.length > 0) && (
        <section className="top-offers py-3 md:py-5 bg-white">
          <div className="container">
            <div className="flex justify-between items-center mb-4 md:mb-6">
              <h2 className="font-bold text-3xl">Builders</h2>
            </div>
            
            {loading ? (
              <div className="flex gap-6 overflow-hidden">
                {Array(4).fill().map((_, i) => (
                  <div key={i} style={{ width: '320px', flexShrink: 0 }}>
                    <ShimmerCard />
                  </div>
                ))}
              </div>
            ) : (
              <div className="marquee-container">
                <div className="marquee-track">
                  {/* Repeat the array enough times to fill the screen for seamless scrolling */}
                  {Array(10).fill(builders).flat().map((builder, idx) => (
                    <div key={`${builder._id}-${idx}`} style={{ width: '320px', flexShrink: 0 }}>
                      <BuilderCard builder={builder} />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Added today */}
      {addedToday.length > 0 && (
        <section className="added-today py-3 md:py-5 bg-white border-t border-gray-100">
          <div className="container">
            <div className="flex justify-between items-center mb-4 md:mb-6">
              <div className="flex items-center gap-6">
                <h2 className="font-bold text-3xl">Added today</h2>
                <div className="flex gap-2">
                  <button className="text-primary text-sm font-semibold border border-primary rounded-full px-4 py-1">Recent</button>
                </div>
              </div>
              <Link to="/properties" className="text-muted font-semibold flex items-center gap-1 hover:text-primary">&rarr; View all</Link>
            </div>
            
            <div className="grid grid-cols-12 gap-6">
              {/* Large Left Card */}
              {addedToday[0] && (() => {
                const p = addedToday[0];
                const isW = p.isWealthProperty;
                let img = getDefaultPropertyImage(p.propertyType, p.subCategory);
                if (isW && p.photo) {
                  let rawPhoto = Array.isArray(p.photo) ? p.photo[0] : p.photo;
                  img = p.newImageUrl || (rawPhoto.startsWith('http') ? rawPhoto : getImageUrl(rawPhoto, true));
                }
                else if (!isW && p.images?.length > 0) img = getImageUrl(p.images[0]);
                
                const title = isW ? ((p.propertyType || 'Property') + (p.location ? ` in ${p.location.split(',')[0].trim()}` : '')) : p.title;
                const forStatus = isW ? 'SALE' : p.for?.toUpperCase();
                const city = isW ? p.location : (p.address?.line1 ? `${p.address.line1}, ${p.address?.city}` : p.address?.city);
                
                const priceValue = isW ? p.price : p.price?.total;
                const formattedPrice = priceValue && Number(priceValue) !== 0
                  ? `₹${Number(priceValue).toLocaleString('en-IN')}`
                  : 'On Request';
                  
                const link = isW ? `/wealth-property/${generateSlug(title)}/${p._id}` : `/property/${generateSlug(title)}/${p._id}`;
                
                return (
                  <div 
                    className="col-span-12 md:col-span-7 added-card relative rounded-xl overflow-hidden h-full flex flex-col justify-end p-6 text-white cursor-pointer hover:shadow-2xl transition-all" 
                    style={{minHeight: '500px'}}
                    onClick={() => navigate(link)}
                  >
                    <img src={img} alt="Added 1" className="absolute inset-0 w-full h-full object-cover z-0" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent opacity-80 z-10"></div>
                    
                    <div className="absolute top-4 left-4 z-20">
                      <span className="badge badge-blue">New</span>
                    </div>

                    <div className="relative z-20 flex justify-between items-end">
                      <div>
                        <p className="text-sm font-semibold mb-1 uppercase tracking-wider">FOR {forStatus}</p>
                        <h3 className="font-bold text-2xl mb-2 line-clamp-1">
                          <Link to={link} className="text-white hover:text-primary">{title}</Link>
                        </h3>
                        <p className="text-sm opacity-90 flex items-center gap-1"><FiMapPin /> {city}</p>
                      </div>
                      <Link to={link} className="btn bg-primary text-white py-2 px-4 rounded-lg font-bold flex items-center gap-2">
                        {formattedPrice} <FiHeart />
                      </Link>
                    </div>
                  </div>
                );
              })()}

              {/* Right Side Stacked Cards */}
              <div className="col-span-12 md:col-span-5 flex flex-col gap-6">
                {addedToday[1] && (() => {
                  const p = addedToday[1];
                  const isW = p.isWealthProperty;
                  let img = getDefaultPropertyImage(p.propertyType, p.subCategory);
                  if (isW && p.photo) {
                  let rawPhoto = Array.isArray(p.photo) ? p.photo[0] : p.photo;
                  img = p.newImageUrl || (rawPhoto.startsWith('http') ? rawPhoto : getImageUrl(rawPhoto, true));
                }
                  else if (!isW && p.images?.length > 0) img = getImageUrl(p.images[0]);
                  
                  const title = isW ? ((p.propertyType || 'Property') + (p.location ? ` in ${p.location.split(',')[0].trim()}` : '')) : p.title;
                  const forStatus = isW ? 'SALE' : p.for?.toUpperCase();
                  const city = isW ? p.location : p.address?.city;
                  
                  const priceValue = isW ? p.price : p.price?.total;
                  const formattedPrice = priceValue && Number(priceValue) !== 0
                    ? `₹${Number(priceValue).toLocaleString('en-IN')}`
                    : 'On Request';
                    
                  const link = isW ? `/wealth-property/${generateSlug(title)}/${p._id}` : `/property/${generateSlug(title)}/${p._id}`;
                  
                  return (
                    <div 
                      className="added-card relative rounded-xl overflow-hidden flex-1 flex flex-col justify-end p-6 text-white cursor-pointer hover:shadow-2xl transition-all" 
                      style={{minHeight: '235px'}}
                      onClick={() => navigate(link)}
                    >
                      <img src={img} alt="Added 2" className="absolute inset-0 w-full h-full object-cover z-0" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent opacity-80 z-10"></div>
                      
                      <div className="relative z-20 flex justify-between items-end">
                        <div>
                          <p className="text-sm font-semibold mb-1 uppercase tracking-wider">FOR {forStatus}</p>
                          <h3 className="font-bold text-xl mb-1 line-clamp-1">
                            <Link to={link} className="text-white hover:text-primary">{title}</Link>
                          </h3>
                          <p className="text-sm opacity-90 flex items-center gap-1"><FiMapPin /> {city}</p>
                        </div>
                        <Link to={link} className="btn bg-primary text-white py-2 px-4 rounded-lg font-bold flex items-center gap-2">
                          {formattedPrice} <FiHeart />
                        </Link>
                      </div>
                    </div>
                  );
                })()}

                {addedToday[2] && (() => {
                  const p = addedToday[2];
                  const isW = p.isWealthProperty;
                  let img = getDefaultPropertyImage(p.propertyType, p.subCategory);
                  if (isW && p.photo) {
                  let rawPhoto = Array.isArray(p.photo) ? p.photo[0] : p.photo;
                  img = p.newImageUrl || (rawPhoto.startsWith('http') ? rawPhoto : getImageUrl(rawPhoto, true));
                }
                  else if (!isW && p.images?.length > 0) img = getImageUrl(p.images[0]);
                  
                  const title = isW ? ((p.propertyType || 'Property') + (p.location ? ` in ${p.location.split(',')[0].trim()}` : '')) : p.title;
                  const forStatus = isW ? 'SALE' : p.for?.toUpperCase();
                  const city = isW ? p.location : p.address?.city;
                  
                  const priceValue = isW ? p.price : p.price?.total;
                  const formattedPrice = priceValue && Number(priceValue) !== 0
                    ? `₹${Number(priceValue).toLocaleString('en-IN')}`
                    : 'On Request';
                    
                  const link = isW ? `/wealth-property/${generateSlug(title)}/${p._id}` : `/property/${generateSlug(title)}/${p._id}`;
                  
                  return (
                    <div 
                      className="added-card relative rounded-xl overflow-hidden flex-1 flex flex-col justify-end p-6 text-white cursor-pointer hover:shadow-2xl transition-all" 
                      style={{minHeight: '235px'}}
                      onClick={() => navigate(link)}
                    >
                      <img src={img} alt="Added 3" className="absolute inset-0 w-full h-full object-cover z-0" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent opacity-80 z-10"></div>

                      <div className="relative z-20 flex justify-between items-end">
                        <div>
                          <p className="text-sm font-semibold mb-1 uppercase tracking-wider">FOR {forStatus}</p>
                          <h3 className="font-bold text-xl mb-1 line-clamp-1">
                            <Link to={link} className="text-white hover:text-primary">{title}</Link>
                          </h3>
                          <p className="text-sm opacity-90 flex items-center gap-1"><FiMapPin /> {city}</p>
                        </div>
                        <Link to={link} className="btn bg-primary text-white py-2 px-4 rounded-lg font-bold flex items-center gap-2">
                          {formattedPrice} <FiHeart />
                        </Link>
                      </div>
                    </div>
                  );
                })()}
              </div>

            </div>
          </div>
        </section>
      )}

      {/* Platform Statistics Section - Redesigned & Moved to Bottom */}
      {siteStats && (
        <section className="py-16 md:py-24 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative">
          <div className="container mx-auto px-4 max-w-6xl relative z-10 space-y-8 md:space-y-12">
            
            {/* User Stats Card */}
            <div className="bg-white rounded-[40px] p-8 md:p-14 shadow-2xl">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12">
                <div className="max-w-xl">
                  <h2 className="text-4xl md:text-5xl font-extrabold text-slate-800 tracking-tight mb-4 leading-tight">We only deliver results.</h2>
                  <p className="text-slate-500 text-lg">Connecting buyers, sellers, and builders across the state. No excuses, just seamless real estate transactions.</p>
                </div>
                <div className="mt-8 md:mt-0 flex gap-4 flex-wrap">
                   <Link to="/properties" className="px-6 py-3 rounded-full border-2 border-slate-200 text-slate-700 font-bold hover:bg-slate-50 transition flex items-center gap-2">
                     <FiSearch /> Browse
                   </Link>
                   <Link to="/add-property" className="px-6 py-3 rounded-full bg-slate-800 text-white font-bold hover:bg-slate-900 transition shadow-lg flex items-center gap-2">
                     Get Started
                   </Link>
                </div>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-4">
                {[
                  { label: 'Buyers', count: siteStats.users?.buyer || 0, desc: 'Active users finding homes.' },
                  { label: 'Sellers', count: siteStats.users?.seller || 0, desc: 'Trusting us with listings.' },
                  { label: 'Developers', count: siteStats.users?.builder || 0, desc: 'Building communities.' },
                  { label: 'Agents', count: siteStats.users?.agent || 0, desc: 'Facilitating deals.' },
                ].map((stat, idx) => (
                  <div key={idx} className="pr-4 border-r border-slate-100 last:border-0 relative">
                    <h3 className="text-5xl md:text-6xl font-black text-slate-800 tracking-tighter mb-4 leading-none flex items-baseline gap-1">
                      {stat.count}
                      {stat.count > 0 && <span className="text-4xl md:text-5xl text-slate-700">+</span>}
                    </h3>
                    <p className="text-lg font-bold text-slate-700 mb-2">{stat.label}</p>
                    <p className="text-sm text-slate-500 leading-relaxed max-w-[160px]">{stat.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Property Stats Card */}
            <div className="bg-white rounded-[40px] p-8 md:p-14 shadow-2xl flex flex-col lg:flex-row gap-12 items-center">
              <div className="lg:w-1/2">
                 <h2 className="text-4xl md:text-5xl font-black text-slate-800 tracking-tight mb-5 leading-tight">Let's build<br/>something great.</h2>
                 <p className="text-slate-500 text-lg leading-relaxed mb-6 max-w-md">
                   Real-time overview of our property inventory. Track available and sold-out properties across all major categories easily.
                 </p>
              </div>
              
              <div className="lg:w-1/2 grid grid-cols-1 sm:grid-cols-2 gap-6 w-full">
                {Object.keys(siteStats.properties || {}).length === 0 ? (
                    <div className="col-span-2 text-center p-8 bg-slate-50 rounded-3xl border border-dashed border-slate-200">
                        <p className="text-slate-500">No properties available yet.</p>
                    </div>
                ) : (
                    Object.keys(siteStats.properties || {}).sort().map((type, idx) => {
                      const props = siteStats.properties[type];
                      const icons = [<FiActivity size={22}/>, <FiBriefcase size={22}/>, <FiHeart size={22}/>, <FiMapPin size={22}/>];
                      const icon = icons[idx % icons.length];
                      
                      return (
                        <div key={type} className="flex flex-col group p-6 rounded-3xl bg-slate-50 hover:bg-slate-100 transition-colors border border-slate-100 hover:border-slate-200">
                          <div className="flex items-center gap-4 mb-4">
                            <div className="bg-white text-slate-600 w-12 h-12 rounded-2xl flex items-center justify-center shadow-sm group-hover:bg-primary group-hover:text-white transition-all transform group-hover:scale-110">
                              {icon}
                            </div>
                            <h3 className="text-5xl font-black text-slate-800 tracking-tighter leading-none flex items-baseline gap-1">
                              {props.uploaded}
                              {props.uploaded > 0 && <span className="text-4xl text-slate-700">+</span>}
                            </h3>
                          </div>
                          <div>
                            <p className="text-lg font-bold text-slate-800 mb-1">{type}{(type.endsWith('Properties') || type.endsWith('Projects')) ? '' : ' Properties'}</p>
                            <div className="flex items-center gap-2 text-sm font-medium">
                              <span className="text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-md">{props.available} Available</span>
                              <span className="text-slate-300">•</span>
                              <span className="text-rose-500 bg-rose-50 px-2 py-0.5 rounded-md">{props.sold} Sold</span>
                            </div>
                          </div>
                        </div>
                      );
                    })
                )}
              </div>
            </div>

          </div>
        </section>
      )}
    </div>
  );
};

export default DesktopHome;
